import type { ReasoningStep, SimpleAgent } from '@demo-portal/shared';
import React, { useState } from 'react';

interface AgentCardProps {
  agent: SimpleAgent;
  isActive?: boolean;
  reasoning?: ReasoningStep | null;
  primaryColor?: string;
  isOrchestrator?: boolean;
  orchestrationRole?: string;
  onClick?: () => void;
}

const AgentCard: React.FC<AgentCardProps> = ({
  agent,
  isActive = false,
  reasoning,
  primaryColor = '#3498db',
  isOrchestrator = false,
  orchestrationRole,
  onClick
}) => {
  const [showDetails, setShowDetails] = useState(false);

  const handleCardClick = () => {
    setShowDetails(!showDetails);
    onClick?.();
  };

  // Orchestrator styling
  const getCardStyling = () => {
    if (isOrchestrator) {
      return {
        background: isActive 
          ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
          : 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)',
        border: isActive 
          ? '2px solid #667eea' 
          : '2px solid rgba(102, 126, 234, 0.3)',
        color: isActive ? 'white' : '#2c3e50',
        boxShadow: isActive 
          ? '0 8px 25px rgba(102, 126, 234, 0.4)' 
          : '0 4px 15px rgba(102, 126, 234, 0.2)',
      };
    } else {
      return {
        background: isActive ? '#e8f4fd' : '#f8f9fa',
        borderLeft: `4px solid ${isActive ? '#ff9800' : primaryColor}`,
        color: '#2c3e50',
        boxShadow: isActive ? '0 6px 20px rgba(0,0,0,0.15)' : '0 2px 8px rgba(0,0,0,0.05)',
      };
    }
  };

  const cardStyling = getCardStyling();

  return (
    <div
      style={{
        marginBottom: '20px',
        padding: '18px',
        borderRadius: isOrchestrator ? '15px' : '12px',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        transform: isActive ? 'translateY(-2px)' : 'none',
        position: 'relative',
        overflow: 'hidden',
        ...cardStyling,
      }}
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        if (!isActive) {
          if (isOrchestrator) {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f0f4ff 0%, #e8f0ff 100%)';
            e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.3)';
          } else {
            e.currentTarget.style.background = '#e9ecef';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
          }
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        if (!isActive) {
          if (isOrchestrator) {
            e.currentTarget.style.background = 'linear-gradient(135deg, #f8f9ff 0%, #f0f4ff 100%)';
            e.currentTarget.style.boxShadow = '0 4px 15px rgba(102, 126, 234, 0.2)';
          } else {
            e.currentTarget.style.background = '#f8f9fa';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
          }
          e.currentTarget.style.transform = 'none';
        }
      }}
    >
      {/* Orchestrator background pattern */}
      {isOrchestrator && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(255,255,255,0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 0%, transparent 50%)',
          pointerEvents: 'none',
          opacity: isActive ? 1 : 0.5,
        }} />
      )}

      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        marginBottom: '10px',
        position: 'relative',
        zIndex: 1,
      }}>
        <span style={{ 
          fontSize: '1.4em', 
          marginRight: '10px',
          filter: isOrchestrator ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))' : 'none',
        }}>
          {agent.avatar}
        </span>
        <div style={{ flex: 1 }}>
          <div style={{
            fontWeight: 'bold',
            color: isOrchestrator && isActive ? 'white' : cardStyling.color,
            fontSize: '1em',
            textShadow: isOrchestrator && isActive ? '0 1px 2px rgba(0,0,0,0.3)' : 'none',
          }}>
            {agent.name}
          </div>
          {isOrchestrator && (
            <div style={{
              fontSize: '0.75em',
              color: isActive ? 'rgba(255,255,255,0.9)' : '#667eea',
              fontWeight: 'bold',
              marginTop: '2px',
            }}>
              🎯 ORCHESTRATOR
            </div>
          )}
        </div>
        
        {/* Orchestration role badge */}
        {orchestrationRole && !isOrchestrator && (
          <div style={{
            background: 'rgba(102, 126, 234, 0.1)',
            color: '#667eea',
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.7em',
            fontWeight: 'bold',
            border: '1px solid rgba(102, 126, 234, 0.2)',
          }}>
            {orchestrationRole}
          </div>
        )}
      </div>

      <div style={{
        color: isOrchestrator && isActive ? 'rgba(255,255,255,0.9)' : '#666',
        fontSize: '0.9em',
        lineHeight: '1.4',
        marginBottom: '8px',
        position: 'relative',
        zIndex: 1,
      }}>
        {agent.role}
      </div>

      {showDetails && (
        <div style={{
          marginTop: '12px',
          paddingTop: '12px',
          borderTop: `1px solid ${isOrchestrator && isActive ? 'rgba(255,255,255,0.3)' : '#dee2e6'}`,
          animation: 'fadeIn 0.3s ease-in',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{ marginBottom: '10px' }}>
            <div style={{
              fontWeight: 'bold',
              color: isOrchestrator && isActive ? 'rgba(255,255,255,0.9)' : '#495057',
              fontSize: '0.85em',
              marginBottom: '4px'
            }}>
              Data Sources:
            </div>
            <div style={{ 
              fontSize: '0.8em', 
              color: isOrchestrator && isActive ? 'rgba(255,255,255,0.8)' : '#6c757d', 
              lineHeight: '1.3' 
            }}>
              {agent.dataSources.map((source, index) => (
                <div key={index}>• {source}</div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: '10px' }}>
            <div style={{
              fontWeight: 'bold',
              color: isOrchestrator && isActive ? 'rgba(255,255,255,0.9)' : '#495057',
              fontSize: '0.85em',
              marginBottom: '4px'
            }}>
              Tools:
            </div>
            <div style={{ 
              fontSize: '0.8em', 
              color: isOrchestrator && isActive ? 'rgba(255,255,255,0.8)' : '#6c757d', 
              lineHeight: '1.3' 
            }}>
              {agent.tools.map((tool, index) => (
                <div key={index}>• {tool}</div>
              ))}
            </div>
          </div>

          {/* Orchestration-specific details */}
          {orchestrationRole && !isOrchestrator && (
            <div style={{ marginBottom: '10px' }}>
              <div style={{
                fontWeight: 'bold',
                color: '#495057',
                fontSize: '0.85em',
                marginBottom: '4px'
              }}>
                Orchestration Role:
              </div>
              <div style={{ 
                fontSize: '0.8em', 
                color: '#667eea', 
                lineHeight: '1.3',
                fontStyle: 'italic',
              }}>
                {orchestrationRole}
              </div>
            </div>
          )}
        </div>
      )}

      {reasoning && (
        <div style={{
          marginTop: '10px',
          padding: '10px',
          background: isOrchestrator && isActive 
            ? 'rgba(255,255,255,0.15)' 
            : '#fff',
          borderRadius: '8px',
          borderLeft: `3px solid ${isOrchestrator ? '#667eea' : primaryColor}`,
          animation: 'fadeIn 0.5s ease-in',
          border: isOrchestrator && isActive 
            ? '1px solid rgba(255,255,255,0.2)' 
            : 'none',
          position: 'relative',
          zIndex: 1,
        }}>
          <div style={{ 
            fontSize: '0.9em', 
            lineHeight: '1.4',
            color: isOrchestrator && isActive ? 'white' : '#2c3e50',
          }}>
            <strong>{reasoning.step}:</strong><br />
            {reasoning.reasoning}<br />
            <div style={{
              color: isOrchestrator && isActive ? '#4ade80' : '#27ae60',
              fontWeight: 500,
              marginTop: '8px'
            }}>
              → {reasoning.decision}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentCard;